/**
 * Printing a served document without leaving the screen (§12.6, H107).
 *
 * « PDF » is the browser's print of the HTML export: the screen reads
 * `export.html`, loads it in a hidden, isolated frame and asks THAT frame to
 * print. The operator stays where they were, and the box the browser opens is
 * the one where « Enregistrer en PDF » lives.
 *
 * ## The sandbox is `allow-same-origin allow-modals`, and never `allow-scripts`
 *
 * The document carries the words of a person, who can type anything, so it must
 * never run a script under the origin of the administration (§12.6). Two tokens
 * are needed to print it, and each was checked in Chrome rather than guessed:
 *
 * - WITHOUT `allow-modals`, Chrome answers the call with « Ignored call to
 *   'print()'. The document is sandboxed, and the 'allow-modals' keyword is not
 *   set. » and no box opens. `print()` is a modal, and a sandbox forbids modals
 *   by default.
 * - WITHOUT `allow-same-origin`, the frame gets an opaque origin, and the parent
 *   cannot even READ `contentWindow.print`: « SecurityError: Blocked a frame
 *   with origin ... from accessing a cross-origin frame ». `print` is not one of
 *   the few members a cross-origin window exposes.
 *
 * `allow-same-origin` is only dangerous NEXT TO `allow-scripts`: together they
 * let a framed script reach its parent and lift its own sandbox. Alone, it lets
 * the parent reach the frame, and nothing inside the frame runs at all - Chrome
 * logs « Blocked script execution in 'about:srcdoc' because the document's
 * frame is sandboxed and the 'allow-scripts' permission is not set » for a
 * `<script>` planted in the document, and the planted script did not run. The
 * only code touching the frame is this file's, and it only calls `print()`.
 *
 * ## `srcdoc` rather than a Blob address
 *
 * The HTML is already in memory (it was fetched with the operator's cookie), and
 * `srcdoc` hands it to the frame without minting an address that would have to
 * be revoked. The frame is kept until `afterprint` or the next print, never
 * removed as `print()` returns: whether a browser's preview still reads the
 * frame at that point is its own business, and a frame of zero pixels costs
 * nothing to keep.
 */

/** The two tokens that print, and the one that is never there. */
export const PRINT_FRAME_SANDBOX = 'allow-same-origin allow-modals';

/** The mark of the frame, for the next print to find and for the tests. */
export const PRINT_FRAME_TEST_ID = 'sessions-export-cadre';

/**
 * Hidden the way a frame can be hidden and still print: zero-sized, fixed, and
 * out of the tab order - but never `display: none`, which leaves the document
 * unlaid and the preview blank.
 */
const HIDDEN_FRAME_STYLE =
  'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';

/** Remove what a previous print left behind. */
function removeFrames(owner: Document): void {
  for (const node of owner.querySelectorAll(`iframe[data-testid="${PRINT_FRAME_TEST_ID}"]`)) {
    node.remove();
  }
}

/**
 * Load `html` in an isolated frame and open the browser's print box on it.
 *
 * Resolves once `print()` has been called, which is when the entry of the menu
 * stops saying « Préparation du PDF... ». Rejects when the frame cannot be
 * reached, so the menu can say so instead of pretending.
 */
export function printInIsolatedFrame(html: string, owner: Document = document): Promise<void> {
  removeFrames(owner);
  const frame = owner.createElement('iframe');
  frame.setAttribute('sandbox', PRINT_FRAME_SANDBOX);
  frame.setAttribute('data-testid', PRINT_FRAME_TEST_ID);
  frame.setAttribute('aria-hidden', 'true');
  frame.setAttribute('tabindex', '-1');
  frame.setAttribute('title', 'Document à imprimer');
  frame.setAttribute('style', HIDDEN_FRAME_STYLE);

  return new Promise<void>((resolve, reject) => {
    frame.addEventListener(
      'load',
      () => {
        const view = frame.contentWindow;
        if (view === null) {
          frame.remove();
          reject(new Error('The print frame has no window.'));
          return;
        }
        view.addEventListener('afterprint', () => frame.remove(), { once: true });
        // The embedded font has to be decoded before the page is laid out for
        // print, or the first preview is drawn in the fallback face.
        const fonts = frame.contentDocument?.fonts;
        const ready = fonts === undefined ? Promise.resolve() : fonts.ready.then(() => undefined);
        ready.then(
          () => {
            try {
              view.print();
              resolve();
            } catch (error) {
              frame.remove();
              reject(error);
            }
          },
          (error: unknown) => {
            frame.remove();
            reject(error);
          },
        );
      },
      { once: true },
    );
    frame.srcdoc = html;
    owner.body.append(frame);
  });
}
